import { withTransaction } from '@elastic/apm-rum-react'
import { HeimdallService, Instagram, Loader, LogService, MediaKit, MySquid, onConfigChange, onUserChange, User } from '@hub/shared'
import CardMediaKit from '@src/components/card-mediakit/card-mediakit.component'
import CardToCreateMediaKit from '@src/components/card-to-create-instagram-mediakit/card-to-create-instagram-mediakit.component'
import EmptyMediaKit from '@src/components/empty-mediakit/empty-mediakit.component'
import OnboardingMediaKit from '@src/components/onboarding-mediakit/onboarding-mediakit.component'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { SubscriptionLike } from 'rxjs'
import './list-mediakit.page.scoped.scss'

const ListMediaKitPageComponent = (props: any) => {
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onConfigChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const logService = useMemo(() => new LogService(), [])
  const heimdallService = useMemo(() => new HeimdallService(), [])
  const [t] = useTranslation('listMediaKit')
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [configs, setConfig] = useState<MySquid | null>(null)
  const [mediakit, setMediaKit] = useState<Array<MediaKit> | null>(null)
  const [mediakitToCreate, setMediakitToCreate] = useState<Array<Instagram>>([])
  const [showOmboarding, setShowOmboarding] = useState(false)

  logService.init('Media Kit List Page')

  const handleGetMediaKit = useCallback(() => {
    heimdallService
      .getAllMediaKit('1')
      .then((response) => {
        setShowOmboarding(response?.some((i: any) => i.status) || !response?.length)
        const profilesWithoutMediakit: Instagram[] =
          user?.instagram?.filter((socialProfile) => !response.some((mediakit: MediaKit) => mediakit?.profileId === socialProfile?.id)) ||
          []
        setMediaKit(response)
        setMediakitToCreate(profilesWithoutMediakit!)
      })
      .catch((error) => {
        logService.error({
          message: 'Can not get influencer media kits',
          error,
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [heimdallService, logService, user])

  useEffect(() => {
    onConfigChangeSubscription.current = onConfigChange.subscribe(async (configChanged: MySquid | any) => {
      setConfig(configChanged)
    })

    onUserChangeSubscription.current = onUserChange.subscribe(async (newUser: User) => {
      setUser(newUser)
    })

    return () => {
      onUserChangeSubscription?.current?.unsubscribe()
      onConfigChangeSubscription?.current?.unsubscribe()
    }
  }, [onConfigChangeSubscription])

  useEffect(() => {
    if (user) {
      handleGetMediaKit()
    }
  }, [handleGetMediaKit, user])

  if (loading) {
    return (
      <div className="media-kit-list loader-wrapper">
        <Loader size="big" />
      </div>
    )
  }

  if (!user?.instagram?.length) {
    return <EmptyMediaKit />
  }

  if (showOmboarding) {
    return <OnboardingMediaKit setShowOmboarding={setShowOmboarding} />
  }

  return (
    <div className="box media-kit-list">
      <div className="list-page-mediakit">
        <div className="header-list-mediakit">
          <h5>{t('title')}</h5>
        </div>
        <main>
          <p>{t('text')}</p>
          {mediakit?.length ? (
            <div>
              <h5>{t('created')}</h5>
              <div className="list-cards-mediakit">
                {mediakit?.map((item) => (
                  <div className="item-card-list-mediakit" key={item?._id}>
                    <CardMediaKit mediakit={item} onChangeMediakit={() => handleGetMediaKit()} className="item-card-list-mediakit" />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {mediakitToCreate?.length ? (
            <div>
              {mediakit?.length ? <h5>{t('toCreate')}</h5> : null}
              <div className="list-cards-to-create-mediakit">
                {mediakitToCreate?.map((item, i) => (
                  <div className="item-card-to-create-list-mediakit" key={`${item?.id}-${i}`}>
                    <CardToCreateMediaKit user={item} className="item-card-to-create-list-mediakit" />
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </main>
      </div>
    </div>
  )
}

export default withTransaction('ListMediaKitPageComponent', 'component')(ListMediaKitPageComponent)
